import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Avatar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TaskIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import HandshakeIcon from '@mui/icons-material/Handshake';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SendIcon from '@mui/icons-material/Send';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GavelIcon from '@mui/icons-material/Gavel';
import HomeIcon from '@mui/icons-material/Home';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTask } from '../../../contexts/TaskContext';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';
import CreateNewTask from '../../../components/common/CreateNewTask';
import EmptyTaskCard from '../../../components/common/EmptyTaskCard';
import { useModal } from '../../../contexts/ModalContext';
import { LeadTabPanelLoader, LeadTabInlineProgress } from './components/LeadTabPanelLoader';
import { useLeadTabInfiniteScroll } from './components/useLeadTabInfiniteScroll';
import {
  getTaskItemTypeLabel,
  getTaskPriorityLabel,
  getTaskStatusLabel,
} from '../../../utils/lead_helper_v2';

/* ─────────────────────────── constants ─────────────────────────── */

const priorityConfig = {
  low:    { color: '#22C55E', bg: '#F0FDF4' },
  medium: { color: '#3B82F6', bg: '#EFF6FF' },
  high:   { color: '#F97316', bg: '#FFF7ED' },
  urgent: { color: '#EF4444', bg: '#FEF2F2' },
};

const TASK_TYPE_MAPPING = {
  contact_call:         { icon: <PhoneIcon fontSize="small" />,       color: '#EF4444', label: 'Call' },
  contact_meeting:      { icon: <HandshakeIcon fontSize="small" />,   color: '#10B981', label: 'Meeting' },
  meeting:              { icon: <BusinessIcon fontSize="small" />,    color: '#EC4899', label: 'Meeting' },
  lead_followup:        { icon: <PhoneIcon fontSize="small" />,       color: '#EF4444', label: 'Follow-up' },
  payment_followup:     { icon: <AttachMoneyIcon fontSize="small" />, color: '#8B5CF6', label: 'Payment' },
  document_collection:  { icon: <DescriptionIcon fontSize="small" />, color: '#10B981', label: 'Document' },
  contract_signing:     { icon: <AssignmentIcon fontSize="small" />,  color: '#F59E0B', label: 'Contract' },
  send_proposal:        { icon: <SendIcon fontSize="small" />,        color: '#F59E0B', label: 'Proposal' },
  site_visit:           { icon: <HomeIcon fontSize="small" />,        color: '#8B5CF6', label: 'Site Visit' },
  property_inspection:  { icon: <BusinessIcon fontSize="small" />,    color: '#8B5CF6', label: 'Inspection' },
  property_photoshoot:  { icon: <CameraAltIcon fontSize="small" />,   color: '#8B5CF6', label: 'Photoshoot' },
  property_valuation:   { icon: <AssessmentIcon fontSize="small" />,  color: '#8B5CF6', label: 'Valuation' },
  legal_verification:   { icon: <GavelIcon fontSize="small" />,       color: '#6366F1', label: 'Legal' },
  whatsapp_message:     { icon: <WhatsAppIcon fontSize="small" />,    color: '#25D366', label: 'WhatsApp' },
  task:                 { icon: <TaskIcon fontSize="small" />,         color: '#64748B', label: 'Task' },
  other:                { icon: <TaskIcon fontSize="small" />,         color: '#64748B', label: 'Other' },
};

const getTaskTypeInfo = (itemType) =>
  TASK_TYPE_MAPPING[itemType] || TASK_TYPE_MAPPING.other;

/* ─────────────────────────── status filter (Call History–style chips) ─────────────────────────── */

const TASK_STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

const taskMatchesStatusTab = (task, tabId) => {
  if (tabId === 'all') return true;
  const s = String(task.status || '').toLowerCase();
  if (tabId === 'pending') return s === 'pending' || s === 'scheduled';
  if (tabId === 'in_progress') return s === 'in_progress' || s === 'update_pending';
  if (tabId === 'overdue') return s === 'overdue';
  if (tabId === 'completed') return s === 'completed';
  if (tabId === 'cancelled') return s === 'cancelled';
  return true;
};

const TASK_FILTER_TAB_THEME = {
  all: {
    border: '#E2E8F0',
    borderActive: '#334155',
    bg: '#FFFFFF',
    bgActive: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)',
    label: '#475569',
    labelActive: '#0F172A',
    badgeBg: '#F1F5F9',
    badgeBgActive: '#E2E8F0',
    badgeColor: '#64748B',
    badgeColorActive: '#0F172A',
    shadow: '0 1px 4px rgba(15,23,42,0.12)',
  },
  pending: {
    border: '#BFDBFE',
    borderActive: '#3B82F6',
    bg: '#EFF6FF',
    bgActive: 'linear-gradient(180deg, #DBEAFE 0%, #BFDBFE 100%)',
    label: '#1D4ED8',
    labelActive: '#1E3A8A',
    badgeBg: '#DBEAFE',
    badgeBgActive: '#93C5FD',
    badgeColor: '#2563EB',
    badgeColorActive: '#1E3A8A',
    shadow: '0 1px 4px rgba(59,130,246,0.25)',
  },
  in_progress: {
    border: '#A7F3D0',
    borderActive: '#10B981',
    bg: '#F0FDF4',
    bgActive: 'linear-gradient(180deg, #D1FAE5 0%, #A7F3D0 100%)',
    label: '#047857',
    labelActive: '#065F46',
    badgeBg: '#D1FAE5',
    badgeBgActive: '#6EE7B7',
    badgeColor: '#059669',
    badgeColorActive: '#064E3B',
    shadow: '0 1px 4px rgba(16,185,129,0.25)',
  },
  overdue: {
    border: '#FDE68A',
    borderActive: '#D97706',
    bg: '#FFFBEB',
    bgActive: 'linear-gradient(180deg, #FEF3C7 0%, #FDE68A 100%)',
    label: '#B45309',
    labelActive: '#92400E',
    badgeBg: '#FEF3C7',
    badgeBgActive: '#FCD34D',
    badgeColor: '#D97706',
    badgeColorActive: '#78350F',
    shadow: '0 1px 4px rgba(217,119,6,0.22)',
  },
  completed: {
    border: '#BBF7D0',
    borderActive: '#22C55E',
    bg: '#F0FDF4',
    bgActive: 'linear-gradient(180deg, #DCFCE7 0%, #BBF7D0 100%)',
    label: '#15803D',
    labelActive: '#14532D',
    badgeBg: '#DCFCE7',
    badgeBgActive: '#86EFAC',
    badgeColor: '#16A34A',
    badgeColorActive: '#14532D',
    shadow: '0 1px 4px rgba(34,197,94,0.22)',
  },
  cancelled: {
    border: '#FECACA',
    borderActive: '#EF4444',
    bg: '#FEF2F2',
    bgActive: 'linear-gradient(180deg, #FEE2E2 0%, #FECACA 100%)',
    label: '#B91C1C',
    labelActive: '#991B1B',
    badgeBg: '#FEE2E2',
    badgeBgActive: '#FCA5A5',
    badgeColor: '#DC2626',
    badgeColorActive: '#7F1D1D',
    shadow: '0 1px 4px rgba(239,68,68,0.25)',
  },
};

const TaskStatusFilterTabs = ({ activeTab, onChange, counts }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      mb: 2,
      flexWrap: 'wrap',
      rowGap: 0.75,
    }}
  >
    {TASK_STATUS_TABS.map((tab) => {
      const n = counts[tab.id] ?? 0;
      const active = activeTab === tab.id;
      const th = TASK_FILTER_TAB_THEME[tab.id] || TASK_FILTER_TAB_THEME.all;
      return (
        <Box
          key={tab.id}
          component="button"
          type="button"
          onClick={() => onChange(tab.id)}
          sx={{
            appearance: 'none',
            border: active ? `1.5px solid ${th.borderActive}` : `1px solid ${th.border}`,
            background: active ? th.bgActive : th.bg,
            color: active ? th.labelActive : th.label,
            borderRadius: '999px',
            px: 1.1,
            py: 0.35,
            fontSize: '0.625rem',
            fontWeight: active ? 800 : 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.35,
            transition: 'background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s',
            boxShadow: active ? th.shadow : 'none',
            '&:hover': {
              borderColor: th.borderActive,
              background: active ? undefined : th.bgActive,
              color: th.labelActive,
              boxShadow: active ? th.shadow : '0 1px 3px rgba(0,0,0,0.06)',
            },
          }}
        >
          {tab.label}
          <Box
            component="span"
            sx={{
              minWidth: 16,
              height: 16,
              px: 0.3,
              borderRadius: '99px',
              fontSize: '0.55rem',
              fontWeight: 800,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: active ? th.badgeBgActive : th.badgeBg,
              color: active ? th.badgeColorActive : th.badgeColor,
            }}
          >
            {n}
          </Box>
        </Box>
      );
    })}
  </Box>
);

/* ─────────────────────────── styles ─────────────────────────── */

const styles = {
  card: (color, isDone) => ({
    position: 'relative',
    mb: 1.5,
    border: '1px solid',
    borderColor: isDone ? '#E2E8F0' : 'transparent',
    borderRadius: '14px',
    background: isDone ? '#FAFAFA' : '#FFFFFF',
    boxShadow: isDone
      ? 'none'
      : '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease, transform 0.15s ease',
    '&:hover': {
      boxShadow: isDone
        ? 'none'
        : '0 4px 20px rgba(0,0,0,0.10)',
      transform: isDone ? 'none' : 'translateY(-1px)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: isDone ? '#CBD5E1' : color,
      borderRadius: '14px 0 0 14px',
    },
  }),

  typeChip: (color) => ({
    height: 22,
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    backgroundColor: `${color}18`,
    color: color,
    border: `1px solid ${color}30`,
    '& .MuiChip-label': { px: 1 },
  }),

  priorityChip: (priority) => {
    const key = String(priority ?? '').toLowerCase().trim();
    return {
      height: 22,
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      backgroundColor: priorityConfig[key]?.bg || '#F1F5F9',
      color: priorityConfig[key]?.color || '#64748B',
      '& .MuiChip-label': { px: 1 },
    };
  },

  taskStatusChip: (status) => {
    const s = String(status ?? '').toLowerCase().trim();
    const base = {
      height: 22,
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      '& .MuiChip-label': { px: 1 },
    };
    if (s === 'completed') {
      return { ...base, backgroundColor: '#F0FDF4', color: '#16A34A' };
    }
    if (s === 'cancelled') {
      return { ...base, backgroundColor: '#FEF2F2', color: '#DC2626' };
    }
    return {
      ...base,
      backgroundColor: '#F1F5F9',
      color: '#475569',
      border: '1px solid #E2E8F0',
    };
  },

  aiChip: {
    height: 22,
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    backgroundColor: '#EEF2FF',
    color: '#4F46E5',
    '& .MuiChip-label': { px: 1 },
    '& .MuiChip-icon': { color: '#4F46E5', fontSize: '0.8rem', ml: 0.5 },
  },

  metaText: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.4,
    color: '#94A3B8',
    fontSize: '0.72rem',
    fontWeight: 500,
  },
};

/* ─────────────────────────── TaskItem ─────────────────────────── */

const TaskItem = ({ task, onEdit, onComplete, handleDeleteTask, index }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { openConfirmModal } = useModal();

  const typeInfo   = getTaskTypeInfo(task.item_type);
  const isDone     = task.status === 'completed';
  const isCancelled= task.status === 'cancelled';
  const isInactive = isDone || isCancelled;

  const formatDate = (d) => d ? dayjs(d).format('MMM D, YYYY · h:mm A') : '—';

  const displayDate = isDone
    ? formatDate(task.completed_at || task.updated_at || task.start_time)
    : isCancelled
    ? formatDate(task.cancelled_at || task.updated_at || task.start_time)
    : formatDate(task.start_time);

  return (
    <Paper
      elevation={0}
      sx={{
        ...styles.card(typeInfo.color, isInactive),
        animationDelay: `${index * 60}ms`,
        '@keyframes fadeUp': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to:   { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'fadeUp 0.3s ease both',
      }}
    >
      <Box sx={{ pl: 2.5, pr: 1.5, py: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>

        {/* Icon avatar */}
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: isInactive ? '#F1F5F9' : `${typeInfo.color}15`,
            color: isInactive ? '#94A3B8' : typeInfo.color,
            flexShrink: 0,
            border: `1.5px solid ${isInactive ? '#E2E8F0' : `${typeInfo.color}30`}`,
            boxShadow: isInactive ? 'none' : `0 2px 8px ${typeInfo.color}25`,
          }}
        >
          {isDone
            ? <CheckCircleIcon fontSize="small" sx={{ color: '#22C55E' }} />
            : typeInfo.icon}
        </Avatar>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: '0.875rem',
                color: isInactive ? '#94A3B8' : '#0F172A',
                textDecoration: isDone ? 'line-through' : 'none',
                textDecorationColor: '#CBD5E1',
                letterSpacing: '-0.01em',
                lineHeight: 1.3,
              }}
            >
              {task.title}
            </Typography>

            <Chip
              label={getTaskItemTypeLabel(task.item_type, task.item_type_verbose)}
              size="small"
              sx={styles.typeChip(isInactive ? '#94A3B8' : typeInfo.color)}
            />

            {task.is_ai_generated && (
              <Chip
                icon={<SmartToyIcon />}
                label="AI"
                size="small"
                sx={styles.aiChip}
              />
            )}
          </Box>

          {/* Description */}
          {task.description && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.78rem',
                color: '#64748B',
                mb: 1.25,
                lineHeight: 1.55,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {task.description}
            </Typography>
          )}

          {/* Meta row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>

            <Box sx={styles.metaText}>
              <CalendarTodayIcon sx={{ fontSize: '0.75rem' }} />
              {displayDate}
            </Box>

            {task.assigned_to_name && (
              <Box sx={styles.metaText}>
                <PersonIcon sx={{ fontSize: '0.8rem' }} />
                {task.assigned_to_name}
              </Box>
            )}

            {/* Chips */}
            <Chip
              label={`${getTaskPriorityLabel(task.priority)} Priority`}
              size="small"
              sx={styles.priorityChip(task.priority)}
            />

            <Chip
              label={getTaskStatusLabel(task.status)}
              size="small"
              sx={styles.taskStatusChip(task.status)}
            />

            {task.created_at && (
              <Box sx={{ ...styles.metaText, ml: 'auto' }}>
                <AccessTimeIcon sx={{ fontSize: '0.75rem' }} />
                Created {dayjs(task.created_at).format('MMM D')}
              </Box>
            )}
          </Box>
        </Box>

        {/* Menu */}
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={isInactive}
          sx={{
            color: '#CBD5E1',
            flexShrink: 0,
            '&:hover': { color: '#64748B', bgcolor: '#F8FAFC' },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            elevation: 0,
            sx: {
              border: '1px solid #E2E8F0',
              borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              minWidth: 160,
            },
          }}
        >
          <MenuItem
            onClick={() => { setAnchorEl(null); onEdit(task); }}
            sx={{ fontSize: '0.82rem', borderRadius: '6px', mx: 0.5 }}
          >
            <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 600 }}>Edit Task</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              openConfirmModal(
                'Delete Task',
                'Are you sure you want to delete this task? This action cannot be undone.',
                () => handleDeleteTask(task.id),
                'Delete'
              );
            }}
            sx={{ fontSize: '0.82rem', color: '#EF4444', borderRadius: '6px', mx: 0.5 }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#EF4444' }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 600 }}>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Paper>
  );
};

/* ─────────────────────────── LeadTasks ─────────────────────────── */

const LeadTasks = ({ leadId, lead = null }) => {
  const {
    tasks,
    tasksPagination,
    fetchLeadTasks,
    loading: ldLoading,
    error: ldError,
  } = useLeadDetails();
  const { completeTask, deleteTask } = useTask();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editTaskData, setEditTaskData] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statusFilterTab, setStatusFilterTab] = useState('all');
  const loadMoreLockRef = useRef(false);

  const loading = ldLoading?.tasks ?? false;
  const error = ldError?.tasks ?? null;

  useEffect(() => {
    if (leadId) fetchLeadTasks(leadId);
  }, [leadId, fetchLeadTasks]);

  useEffect(() => {
    setStatusFilterTab('all');
  }, [leadId]);

  const handleLoadMore = useCallback(async () => {
    if (!leadId || loadMoreLockRef.current || !tasksPagination?.hasNext) return;
    loadMoreLockRef.current = true;
    setLoadingMore(true);
    try {
      await fetchLeadTasks(leadId, {
        page: tasksPagination.currentPage + 1,
        page_size: tasksPagination.pageSize,
      });
    } catch (e) {
      console.error('Error loading more tasks:', e);
    } finally {
      setLoadingMore(false);
      loadMoreLockRef.current = false;
    }
  }, [leadId, tasksPagination, fetchLeadTasks]);

  const sortedTasks = useMemo(() => {
    if (!tasks?.length) return [];
    return [...tasks].sort((a, b) => {
      const aDone = a.status === 'completed';
      const bDone = b.status === 'completed';
      if (aDone !== bDone) return aDone ? 1 : -1;
      if (aDone && bDone) {
        const at = new Date(a.completed_at || a.updated_at);
        const bt = new Date(b.completed_at || b.updated_at);
        return bt - at;
      }
      return new Date(a.start_time) - new Date(b.start_time);
    });
  }, [tasks]);

  const statusTabCounts = useMemo(() => {
    const counts = {
      all: sortedTasks.length,
      pending: 0,
      in_progress: 0,
      overdue: 0,
      completed: 0,
      cancelled: 0,
    };
    sortedTasks.forEach((t) => {
      TASK_STATUS_TABS.forEach((tab) => {
        if (tab.id === 'all') return;
        if (taskMatchesStatusTab(t, tab.id)) counts[tab.id] += 1;
      });
    });
    return counts;
  }, [sortedTasks]);

  const displayedTasks = useMemo(
    () => sortedTasks.filter((t) => taskMatchesStatusTab(t, statusFilterTab)),
    [sortedTasks, statusFilterTab],
  );

  /** Full spinner only when nothing to show; refreshes keep the list (avoids double spinner with load-more). */
  const showInitialTasksLoading = loading && sortedTasks.length === 0;

  const infiniteScrollEnabled =
    Boolean(leadId) &&
    sortedTasks.length > 0 &&
    !loading &&
    Boolean(tasksPagination?.hasNext);

  const loadMoreSentinelRef = useLeadTabInfiniteScroll({
    enabled: infiniteScrollEnabled,
    hasNext: Boolean(tasksPagination?.hasNext),
    isBusy: loading || loadingMore,
    onLoadMore: handleLoadMore,
    watchKey: `${tasksPagination?.currentPage ?? 0}-${sortedTasks.length}`,
  });

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setEditTaskData(null);
    if (leadId) fetchLeadTasks(leadId);
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await completeTask(taskId);
      if (leadId) fetchLeadTasks(leadId);
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      if (leadId) fetchLeadTasks(leadId);
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: 2.5, py: 2.5 }}>

        {/* ── Header + status chips (Call History pattern) ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0F172A', letterSpacing: '-0.02em' }}>
              Tasks
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontWeight: 500, lineHeight: 1.45 }}>
              {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''} on this page
              {tasksPagination?.totalCount != null && tasksPagination.totalCount !== sortedTasks.length
                ? ` · ${tasksPagination.totalCount} total`
                : ''}
            </Typography>
          </Box>
          {sortedTasks.length > 0 && (
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={() => { setEditTaskData(null); setCreateDialogOpen(true); }}
              sx={(theme) => ({
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.8rem',
                borderRadius: '10px',
                px: 2,
                py: 0.75,
                background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                color: theme.palette.getContrastText(theme.palette.primary.main),
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
              })}
            >
              Create Task
            </Button>
          )}
        </Box>

        {sortedTasks.length > 0 && (
          <TaskStatusFilterTabs
            activeTab={statusFilterTab}
            onChange={setStatusFilterTab}
            counts={statusTabCounts}
          />
        )}

        {/* ── Error ── */}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}

        {/* ── Body ── */}
        {showInitialTasksLoading ? (
          <LeadTabPanelLoader />
        ) : sortedTasks.length > 0 && displayedTasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#94A3B8' }}>
              No tasks in this status. Try another filter or scroll to load more.
            </Typography>
          </Box>
        ) : sortedTasks.length > 0 ? (
          displayedTasks.map((task, i) => (
            <TaskItem
              key={task.id}
              task={task}
              index={i}
              onEdit={(t) => { setEditTaskData(t); setCreateDialogOpen(true); }}
              onComplete={handleCompleteTask}
              handleDeleteTask={handleDeleteTask}
            />
          ))
        ) : (
          <EmptyTaskCard
            onAddTask={() => { setEditTaskData(null); setCreateDialogOpen(true); }}
            title="No tasks yet"
            description="Create a task to track follow-ups and actions for this lead."
            buttonText="Create Task"
          />
        )}

        {tasksPagination?.hasNext && sortedTasks.length > 0 && !loading && (
          <>
            <Box
              ref={loadMoreSentinelRef}
              sx={{ height: 4, minHeight: 4, flexShrink: 0 }}
              aria-hidden
            />
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <LeadTabInlineProgress sx={{ mr: 0 }} />
              </Box>
            )}
          </>
        )}

        {sortedTasks.length > 0 && tasksPagination?.totalCount != null && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#CBD5E1', fontWeight: 500 }}>
              Showing {sortedTasks.length} of {tasksPagination.totalCount} tasks
            </Typography>
          </Box>
        )}

        {/* ── Dialog ── */}
        {createDialogOpen && (
          <CreateNewTask
            open={createDialogOpen}
            onClose={handleCloseDialog}
            leadId={leadId}
            initialData={editTaskData}
            lead={lead}
            source="lead"
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default LeadTasks;
